import { branchDate } from './branch-date.js';
export class AppointmentError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'AppointmentError';
    }
}
// ---------------------------------------------------------------------------------------------
// Date arithmetic on YYYY-MM-DD strings
// ---------------------------------------------------------------------------------------------
const DATE_SHAPE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;
export const MINUTES_PER_DAY = 1440;
function parseDate(date) {
    const match = DATE_SHAPE.exec(date);
    if (match === null) {
        throw new AppointmentError('BAD_DATE', `Not a YYYY-MM-DD date: ${JSON.stringify(date)}`);
    }
    const ms = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    // A shape check alone accepts 2026-02-30, which Date silently rolls to 2 March. A promised date
    // that quietly moves is the defect this guards against, so the round trip must match.
    if (formatDate(ms) !== date) {
        throw new AppointmentError('BAD_DATE', `Not a real calendar date: ${date}`);
    }
    return ms;
}
function formatDate(ms) {
    const d = new Date(ms);
    const y = String(d.getUTCFullYear()).padStart(4, '0');
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function requireInt(name, value, min) {
    if (!Number.isInteger(value) || value < min) {
        throw new AppointmentError('BAD_PARAMETER', `${name} must be an integer of at least ${String(min)}, got ${String(value)}`);
    }
}
export function weekdayOf(date) {
    return new Date(parseDate(date)).getUTCDay();
}
export function addCalendarDays(date, days) {
    requireInt('days', Math.abs(days), 0);
    return formatDate(parseDate(date) + days * MS_PER_DAY);
}
/** Whole calendar days from `from` to `to`; negative when `to` is earlier. */
export function calendarDaysBetween(from, to) {
    return Math.round((parseDate(to) - parseDate(from)) / MS_PER_DAY);
}
/**
 * `APT-004`'s "N months from last test". A month is added on the calendar and a day that does not
 * exist in the target month clamps to its last day: 31 January plus one month is 28 February (29 in
 * a leap year), and 29 February 2024 plus a year is 28 February 2025. Rolling over into March would
 * make a recall due "in a year" land a month late for a customer tested at month end.
 */
export function addMonths(date, months) {
    requireInt('months', months, 0);
    const d = new Date(parseDate(date));
    const total = d.getUTCFullYear() * 12 + d.getUTCMonth() + months;
    const year = Math.floor(total / 12);
    const month = total - year * 12;
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return formatDate(Date.UTC(year, month, Math.min(d.getUTCDate(), lastDay)));
}
function validateCalendar(calendar) {
    if (calendar.openWeekdays.length === 0) {
        // With no open weekday there is no Nth working day. Refused here rather than looping forever.
        throw new AppointmentError('NO_OPEN_WEEKDAY', 'A calendar with no open weekday has no working days.');
    }
    for (const weekday of calendar.openWeekdays) {
        if (!Number.isInteger(weekday) || weekday < 0 || weekday > 6) {
            throw new AppointmentError('BAD_PARAMETER', `Not a weekday (0 to 6): ${String(weekday)}`);
        }
    }
    const lists = [
        calendar.countryHolidays,
        calendar.tenantHolidays ?? [],
        calendar.tenantOpenOn ?? [],
    ];
    for (const list of lists) {
        for (const date of list)
            parseDate(date);
    }
}
export function isWorkingDay(calendar, date) {
    validateCalendar(calendar);
    if (!calendar.openWeekdays.includes(weekdayOf(date)))
        return false;
    if (calendar.tenantOpenOn?.includes(date) === true)
        return true;
    if (calendar.tenantHolidays?.includes(date) === true)
        return false;
    return !calendar.countryHolidays.includes(date);
}
/** `from` itself when it is a working day, otherwise the next one. */
export function nextWorkingDayOnOrAfter(calendar, from) {
    let date = from;
    while (!isWorkingDay(calendar, date))
        date = addCalendarDays(date, 1);
    return date;
}
/**
 * The date `days` working days after `from`. `from` is never counted: it is the day of
 * acknowledgement, and Appendix J counts "from acknowledgement". Three working days from a Monday is
 * Thursday, and from a Friday in a Monday to Friday week is the following Wednesday.
 *
 * Zero returns `from` if the shop is open that day and the next working day otherwise: a same-day
 * turnaround acknowledged on a closed day cannot be met on that day. The spec says nothing on zero,
 * so this is a stated assumption and not a rule.
 */
export function addWorkingDays(calendar, from, days) {
    requireInt('days', days, 0);
    if (days === 0)
        return nextWorkingDayOnOrAfter(calendar, from);
    let date = from;
    let remaining = days;
    while (remaining > 0) {
        date = addCalendarDays(date, 1);
        if (isWorkingDay(calendar, date))
            remaining -= 1;
    }
    return date;
}
/** Working days in the half-open range (`from`, `to`]: the inverse of `addWorkingDays`. */
export function workingDaysBetween(calendar, from, to) {
    const span = calendarDaysBetween(from, to);
    if (span < 0)
        throw new AppointmentError('BAD_DATE', `${to} is before ${from}`);
    let count = 0;
    for (let offset = 1; offset <= span; offset += 1) {
        if (isWorkingDay(calendar, addCalendarDays(from, offset)))
            count += 1;
    }
    return count;
}
/**
 * `JOB-004`: the promised date from the lab's turnaround policy. Appendix J: "using the lab's
 * calendar where it differs from the branch's", so a lab calendar wins when there is one and the
 * branch's is the fallback. The turnaround length is the lab's policy and arrives as a parameter.
 *
 * Whether an acknowledgement after a lab's daily cut-off counts as the next day is not in the PRD.
 * It is not modelled; a caller that needs it passes the date it wants counted from.
 */
export function promisedDate(input) {
    const calendar = input.labCalendar ?? input.branchCalendar;
    return {
        date: addWorkingDays(calendar, input.acknowledgedOn, input.turnaroundWorkingDays),
        calendarUsed: input.labCalendar === undefined ? 'branch' : 'lab',
        acknowledgedOn: input.acknowledgedOn,
    };
}
/**
 * As `promisedDate`, starting from an instant. The instant is dated in the **counting calendar's**
 * zone ("the counterparty's timezone"), not the branch's: a lab two zones east that acknowledges at
 * 23:30 branch time is already on its next day.
 */
export function promisedDateFromInstant(input) {
    const calendar = input.labCalendar ?? input.branchCalendar;
    return promisedDate({
        acknowledgedOn: branchDate(input.acknowledgedAt, calendar.timeZone),
        turnaroundWorkingDays: input.turnaroundWorkingDays,
        branchCalendar: input.branchCalendar,
        ...(input.labCalendar === undefined ? {} : { labCalendar: input.labCalendar }),
    });
}
/**
 * `JOB-004`'s "automatic customer notification when it slips". This decides whether it slipped;
 * sending is I/O. A date that moves earlier is not a slip and needs no apology.
 */
export function promisedDateSlip(previous, current) {
    const late = calendarDaysBetween(previous, current);
    return { slipped: late > 0, calendarDaysLate: Math.max(late, 0) };
}
function validateWindow(window) {
    requireInt('opensAtMinute', window.opensAtMinute, 0);
    requireInt('closesAtMinute', window.closesAtMinute, 1);
    if (window.closesAtMinute > MINUTES_PER_DAY || window.opensAtMinute >= window.closesAtMinute) {
        throw new AppointmentError('BAD_PARAMETER', `Opening window ${String(window.opensAtMinute)} to ${String(window.closesAtMinute)} must lie inside one day and be non-empty.`);
    }
}
/**
 * The window the branch trades in on `date`, or `null` when it is closed that day.
 * Override beats weekday hours beats the default. Two overrides covering one date are refused: which
 * of Ramadan hours and a seasonal late opening wins is the shopkeeper's call, and silently taking the
 * first would be a guess.
 */
export function openingWindowOn(schedule, date) {
    // Every override is shape-checked before the closed-day exit, so a malformed one is a fault on
    // every day and not only on the days the branch happens to be open. A range typed backwards
    // covers no date at all, so Ramadan hours would silently never apply and the shop would trade
    // its default window straight through Ramadan: matching nothing is the worst way to fail. The
    // overlap check below stays per date, because two ranges are only ambiguous on a day they both
    // cover, and on a closed day there is no window to choose between.
    for (const override of schedule.overrides ?? []) {
        parseDate(override.from);
        parseDate(override.to);
        if (override.from > override.to) {
            throw new AppointmentError('BAD_PARAMETER', `Opening-hours override ${override.from} to ${override.to} runs backwards and would apply on no day.`);
        }
    }
    if (!isWorkingDay(schedule.calendar, date))
        return null;
    const covering = (schedule.overrides ?? []).filter((o) => o.from <= date && date <= o.to);
    if (covering.length > 1) {
        throw new AppointmentError('BAD_PARAMETER', `More than one opening-hours override covers ${date}.`);
    }
    const window = covering[0]?.window ?? schedule.weekdayHours?.[weekdayOf(date)] ?? schedule.hours;
    validateWindow(window);
    return window;
}
// ---------------------------------------------------------------------------------------------
// Resources, appointment types, slot rules: APT-001, APT-002
// ---------------------------------------------------------------------------------------------
/**
 * A constant as well as a type, so the database check on a resource's kind is built from the same
 * list and not from a second copy. The type is unchanged.
 */
export const RESOURCE_KINDS = ['staff', 'testing_room', 'visiting_optometrist'];
/**
 * Every field of `SlotRules`, checked together. It is one function called from both
 * `bookingRefusals` and `availableSlots` because validating only part of the object made a
 * malformed rule a `BAD_PARAMETER` on an open day and a silent empty slot list on a closed one:
 * `availableSlots` returns before any booking is evaluated when the branch is shut, so a missing
 * buffer or a negative notice went unseen on exactly the days a storefront renders most often.
 */
function validateSlotRules(rules) {
    requireInt('slotStepMinutes', rules.slotStepMinutes, 1);
    requireInt('bufferMinutes', rules.bufferMinutes, 0);
    if (rules.minNoticeMinutes !== undefined)
        requireInt('minNoticeMinutes', rules.minNoticeMinutes, 0);
    if (rules.maxAdvanceDays !== undefined)
        requireInt('maxAdvanceDays', rules.maxAdvanceDays, 0);
}
/** Constant and type for the same reason as `RESOURCE_KINDS`. */
export const APPOINTMENT_STATUSES = ['booked', 'completed', 'cancelled', 'no_show'];
/**
 * The statuses that hold their time, exported so the database's exclusion constraint and this
 * module's `holdsTime` cannot name different sets. A test reads the constraint's SQL against it.
 */
export const TIME_HOLDING_STATUSES = ['booked', 'completed'];
/** Statuses that hold the time. A cancelled or missed slot is free again; a completed one happened. */
function holdsTime(status) {
    return TIME_HOLDING_STATUSES.includes(status);
}
export function minutesBetween(from, to) {
    return calendarDaysBetween(from.date, to.date) * MINUTES_PER_DAY + (to.minute - from.minute);
}
// ---------------------------------------------------------------------------------------------
// An instant and a branch's wall clock, in both directions
// ---------------------------------------------------------------------------------------------
/**
 * Minutes after local midnight at `instant`, in `timeZone`. `hourCycle: 'h23'` and not `hour12:
 * false`, because the latter can answer "24" for midnight in some runtimes and that would put a
 * 00:30 appointment at minute 1470. Digits are read from parts and reassembled, never from a
 * formatted string, for `D-03`'s sake: this is the same discipline as `branchDate`.
 */
function branchMinuteOfDay(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        numberingSystem: 'latn',
    }).formatToParts(new Date(instant));
    const field = (type) => {
        const found = parts.find((p) => p.type === type);
        if (found === undefined) {
            throw new AppointmentError('BAD_PARAMETER', `No ${type} in the formatted time.`);
        }
        return Number(found.value);
    };
    return (field('hour') % 24) * 60 + field('minute');
}
/**
 * What the wall clock at the branch says at `instant`: the branch's date and the minute of its day.
 *
 * **Never derived from UTC.** A Karachi appointment at 01:00 is on the previous UTC date, and a
 * branch at UTC+14 is a whole calendar day ahead of UTC for ten hours of every day. The date comes
 * from `branchDate`, the one place that logic lives, and the empty-zone refusal is its own.
 */
export function branchLocalTime(instant, timeZone) {
    return { date: branchDate(instant, timeZone), minute: branchMinuteOfDay(instant, timeZone) };
}
/**
 * The instant at which the branch's wall clock reads `date` and `minute`. The inverse of
 * `branchLocalTime`, built from it so the two cannot disagree: guess, read the zone's offset there,
 * correct, and read it again at the corrected instant, which settles it across an offset change.
 */
export function resolveBranchLocalTime(date, minute, timeZone) {
    requireInt('minute', minute, 0);
    if (minute >= MINUTES_PER_DAY) {
        throw new AppointmentError('BAD_PARAMETER', `minute must be below ${String(MINUTES_PER_DAY)}.`);
    }
    const wanted = parseDate(date) + minute * 60_000;
    const wallOf = (instant) => {
        const local = branchLocalTime(instant, timeZone);
        return parseDate(local.date) + local.minute * 60_000;
    };
    const offsetAt = (instant) => {
        const whole = instant - (((instant % 60_000) + 60_000) % 60_000);
        return wallOf(whole) - whole;
    };
    const first = wanted - offsetAt(wanted);
    const settled = wanted - offsetAt(first);
    return { instant: settled, exact: wallOf(settled) === wanted };
}
/**
 * Every reason a request cannot be booked, not only the first: the counter shows all of them.
 * Parameter faults throw instead, since they are the caller's bug and not a customer's choice of slot.
 *
 * `APT-001`: calendar per branch and per resource. Double-booking is refused per resource, so one
 * optometrist cannot be in two rooms and one room cannot hold two tests. `now` is supplied only for
 * online booking (`APT-002`); a counter booking passes none and the notice rules do not apply.
 */
export function bookingRefusals(schedule, request, existing, rules, now) {
    validateSlotRules(rules);
    requireInt('durationMinutes', request.type.durationMinutes, 1);
    requireInt('startMinute', request.startMinute, 0);
    parseDate(request.date);
    const refusals = [];
    const refuse = (code, message) => {
        refusals.push({ code, message });
    };
    const end = request.startMinute + request.type.durationMinutes;
    const window = openingWindowOn(schedule, request.date);
    if (window === null) {
        refuse('BRANCH_CLOSED', `The branch is closed on ${request.date}.`);
    }
    else {
        if (request.startMinute < window.opensAtMinute || end > window.closesAtMinute) {
            refuse('OUTSIDE_OPENING_HOURS', 'The appointment does not fit inside the branch opening hours.');
        }
        if ((request.startMinute - window.opensAtMinute) % rules.slotStepMinutes !== 0) {
            refuse('OFF_GRID', `Start is not on the ${String(rules.slotStepMinutes)}-minute slot grid.`);
        }
    }
    if (now !== undefined) {
        const lead = minutesBetween(now, { date: request.date, minute: request.startMinute });
        if (rules.minNoticeMinutes !== undefined && lead < rules.minNoticeMinutes) {
            refuse('TOO_SOON', 'The slot is inside the minimum booking notice.');
        }
        if (rules.maxAdvanceDays !== undefined &&
            calendarDaysBetween(now.date, request.date) > rules.maxAdvanceDays) {
            refuse('TOO_FAR_AHEAD', 'The slot is further ahead than the booking window allows.');
        }
    }
    const ids = new Set();
    for (const resource of request.resources) {
        if (ids.has(resource.id)) {
            refuse('RESOURCE_DUPLICATED', `Resource ${resource.id} is listed twice.`);
        }
        ids.add(resource.id);
        if (resource.branchId !== schedule.branchId) {
            refuse('RESOURCE_WRONG_BRANCH', `Resource ${resource.id} belongs to another branch.`);
        }
        if (resource.visitDates !== undefined && !resource.visitDates.includes(request.date)) {
            refuse('RESOURCE_NOT_VISITING', `Resource ${resource.id} is not in on ${request.date}.`);
        }
    }
    for (const kind of request.type.requiredResourceKinds ?? []) {
        if (!request.resources.some((r) => r.kind === kind)) {
            refuse('RESOURCE_KIND_MISSING', `This appointment type needs a ${kind}.`);
        }
    }
    for (const other of existing) {
        if (!holdsTime(other.status) || other.date !== request.date)
            continue;
        const shared = other.resourceIds.filter((id) => ids.has(id));
        if (shared.length === 0)
            continue;
        // Buffer on both sides: the two padded intervals must not intersect.
        const otherEnd = other.startMinute + other.durationMinutes;
        if (request.startMinute < otherEnd + rules.bufferMinutes &&
            other.startMinute < end + rules.bufferMinutes) {
            refuse('RESOURCE_DOUBLE_BOOKED', `Resource ${shared.join(', ')} is already booked at ${String(other.startMinute)} on ${request.date}.`);
        }
    }
    return refusals;
}
/** Builds a booked appointment or throws the first refusal. */
export function bookAppointment(schedule, request, existing, rules, identity, now) {
    const [first] = bookingRefusals(schedule, request, existing, rules, now);
    if (first !== undefined)
        throw new AppointmentError(first.code, first.message);
    return {
        id: identity.id,
        branchId: schedule.branchId,
        typeId: request.type.id,
        customerId: identity.customerId,
        date: request.date,
        startMinute: request.startMinute,
        durationMinutes: request.type.durationMinutes,
        resourceIds: request.resources.map((r) => r.id),
        status: 'booked',
        version: 1,
    };
}
/**
 * `APT-002`: the start minutes a storefront can offer on `date`. A slot is offered only when
 * `bookingRefusals` would accept it, so the offer and the booking can never disagree.
 */
export function availableSlots(schedule, date, type, resources, existing, rules, now) {
    validateSlotRules(rules);
    requireInt('durationMinutes', type.durationMinutes, 1);
    const window = openingWindowOn(schedule, date);
    if (window === null)
        return [];
    const slots = [];
    for (let start = window.opensAtMinute; start + type.durationMinutes <= window.closesAtMinute; start += rules.slotStepMinutes) {
        const request = { date, startMinute: start, type, resources };
        if (bookingRefusals(schedule, request, existing, rules, now).length === 0)
            slots.push(start);
    }
    return slots;
}
// ---------------------------------------------------------------------------------------------
// Cancellation, no-show, completion
// ---------------------------------------------------------------------------------------------
/*
 * The PRD names no cancellation or no-show rule. These are the three exits from `booked`, all
 * terminal, because "nothing is deleted": a customer who comes back books a new appointment and the
 * old one stays as the record of what happened. Each takes `expectedVersion` for optimistic locking.
 */
function assertBooked(appointment, expectedVersion, to) {
    if (appointment.version !== expectedVersion) {
        throw new AppointmentError('VERSION_CONFLICT', `Appointment ${appointment.id} is at version ${String(appointment.version)}, not ${String(expectedVersion)}.`);
    }
    if (appointment.status !== 'booked') {
        throw new AppointmentError('ILLEGAL_TRANSITION', `Appointment ${appointment.id} is ${appointment.status} and cannot become ${to}.`);
    }
}
/** Cancelling is for before the start. After it the customer did not come: that is `markNoShow`. */
export function cancelAppointment(appointment, expectedVersion, now, options = {}) {
    assertBooked(appointment, expectedVersion, 'cancelled');
    if (options.lateWithinMinutes !== undefined)
        requireInt('lateWithinMinutes', options.lateWithinMinutes, 0);
    const lead = minutesBetween(now, { date: appointment.date, minute: appointment.startMinute });
    if (lead <= 0) {
        throw new AppointmentError('ALREADY_STARTED', 'The appointment has started; record a no-show or completion.');
    }
    return {
        appointment: {
            ...appointment,
            status: 'cancelled',
            version: appointment.version + 1,
            cancelledAt: now,
            ...(options.reason === undefined ? {} : { cancellationReason: options.reason }),
        },
        late: options.lateWithinMinutes !== undefined && lead < options.lateWithinMinutes,
    };
}
/**
 * A no-show can only be recorded once the start time has passed. Marking a future appointment a
 * no-show would free the slot to resell under a customer who may still arrive.
 */
export function markNoShow(appointment, expectedVersion, now) {
    assertBooked(appointment, expectedVersion, 'no_show');
    if (minutesBetween({ date: appointment.date, minute: appointment.startMinute }, now) < 0) {
        throw new AppointmentError('NOT_STARTED', 'The appointment has not started, so it cannot be a no-show.');
    }
    return { ...appointment, status: 'no_show', version: appointment.version + 1 };
}
export function completeAppointment(appointment, expectedVersion) {
    assertBooked(appointment, expectedVersion, 'completed');
    return { ...appointment, status: 'completed', version: appointment.version + 1 };
}
// ---------------------------------------------------------------------------------------------
// Timing a message: APT-003, OPS-140
// ---------------------------------------------------------------------------------------------
/**
 * `OPS-140`: "Nothing automated sends at 2am." A nominal send moment outside the branch's opening
 * window is moved to the nearest permitted moment in `direction`. The PRD says quiet hours are "a
 * configurable window" without a shape, so only the opening window is enforced here; a tenant's
 * extra quiet window would arrive as a narrower `BranchSchedule.hours`.
 *
 * Reminders move `earlier`: a reminder pushed past the appointment is no reminder. Recall moves
 * `later`: a recall can wait a day. Bounded at a year of closed days so a calendar that is shut all
 * year fails loudly instead of spinning.
 */
export function nextSendableMoment(schedule, nominal, direction) {
    const step = direction === 'later' ? 1 : -1;
    let date = nominal.date;
    let minute = nominal.minute;
    for (let attempts = 0; attempts <= 366; attempts += 1) {
        const window = openingWindowOn(schedule, date);
        if (window !== null) {
            if (minute >= window.opensAtMinute && minute < window.closesAtMinute)
                return { date, minute };
            if (direction === 'later' && minute < window.opensAtMinute) {
                return { date, minute: window.opensAtMinute };
            }
            if (direction === 'earlier' && minute >= window.closesAtMinute) {
                return { date, minute: window.closesAtMinute - 1 };
            }
        }
        date = addCalendarDays(date, step);
        minute = direction === 'later' ? 0 : MINUTES_PER_DAY;
    }
    throw new AppointmentError('BRANCH_CLOSED', 'The branch has no open day within a year of this moment.');
}
/**
 * `APT-003`: when a reminder goes out. The lead time is the tenant's choice and the PRD gives no
 * range, so `leadMinutes` is required.
 */
export function reminderSendTime(schedule, appointment, leadMinutes) {
    requireInt('leadMinutes', leadMinutes, 0);
    const total = appointment.startMinute - leadMinutes;
    const dayShift = Math.floor(total / MINUTES_PER_DAY);
    const nominal = {
        date: addCalendarDays(appointment.date, dayShift),
        minute: total - dayShift * MINUTES_PER_DAY,
    };
    return nextSendableMoment(schedule, nominal, 'earlier');
}
/** The calendar day a recall falls due. Recall runs in calendar days (Appendix J). */
export function recallDue(rule) {
    switch (rule.kind) {
        case 'sight_test':
            requireInt('intervalMonths', rule.intervalMonths, 1);
            return { dueOn: addMonths(rule.lastTestOn, rule.intervalMonths) };
        case 'frame_refresh':
            requireInt('intervalMonths', rule.intervalMonths, 1);
            return { dueOn: addMonths(rule.purchasedOn, rule.intervalMonths) };
        case 'warranty_expiry':
            requireInt('leadDays', rule.leadDays, 0);
            return { dueOn: addCalendarDays(rule.warrantyEndsOn, -rule.leadDays) };
        case 'contact_lens': {
            requireInt('units', rule.units, 1);
            requireInt('reorderLeadDays', rule.reorderLeadDays, 0);
            const perUnit = rule.daysPerUnit[rule.modality];
            requireInt(`daysPerUnit.${rule.modality}`, perUnit, 1);
            const supplyDays = rule.units * perUnit;
            const supplyEndsOn = addCalendarDays(rule.purchasedOn, supplyDays);
            // A lead longer than the supply would put the recall before the sale. Clamped to the sale
            // date: the customer is due as soon as they could be, not in the past.
            return {
                dueOn: addCalendarDays(supplyEndsOn, -Math.min(rule.reorderLeadDays, supplyDays)),
                supplyEndsOn,
            };
        }
    }
}
/**
 * `OPS-140` applied to recall: a due date that is a closed day is sent on the next open one. Recall
 * is the retention engine, so a late send is better than a lost one.
 */
export function recallSendDate(schedule, due) {
    return nextWorkingDayOnOrAfter(schedule.calendar, due.dueOn);
}
/**
 * `APT-005`: per-channel consent and per-customer suppression. Evaluated against consent **as it
 * stands when the send runs**, not when the campaign was queued: edge case 57 is consent withdrawn
 * while a recall is queued. Suppression beats consent. `preferred` is tried in order so the
 * customer's chosen channel (`OPS-143`) goes first; a channel they have not consented to is skipped
 * even when it is their preferred one.
 */
export function decideRecall(input) {
    if (input.suppressed)
        return { send: false, reason: 'suppressed' };
    for (const channel of input.preferred) {
        if (input.consented.includes(channel))
            return { send: true, channel };
    }
    return { send: false, reason: 'no_consented_channel' };
}
/**
 * `APT-006`: nudge dates for a contact lens customer, starting at the recall date and repeating
 * until the cap. Cadence and cap are unspecified, so both are required. Each date still goes through
 * `recallSendDate` and `decideRecall` before anything is sent.
 */
export function replenishmentNudgeDates(due, repeatEveryDays, maxNudges) {
    requireInt('repeatEveryDays', repeatEveryDays, 1);
    requireInt('maxNudges', maxNudges, 1);
    return Array.from({ length: maxNudges }, (_, i) => addCalendarDays(due.dueOn, i * repeatEveryDays));
}
/**
 * `APT-006`: a customer whose supply ended before `today` is wearing lenses past their replacement
 * date, which the spec calls a safety issue and not only a revenue one.
 */
export function isReplenishmentLapsed(due, today) {
    return due.supplyEndsOn !== undefined && calendarDaysBetween(due.supplyEndsOn, today) > 0;
}
//# sourceMappingURL=appointments.js.map